import { Link } from "react-router-dom";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import { useAuth } from "@/hooks/useAuth";
import { resolveTeacherIdentity } from "@/lib/profile";
import { useTeacherStats } from "./teacherStats";

const quickActions = [
  { label: "Ders planla", href: "/ogretmen/ders-programim", icon: "ri-calendar-line" },
  { label: "Ödev oluştur", href: "/ogretmen/odevler", icon: "ri-task-line" },
  { label: "Soru ekle", href: "/ogretmen/soru-ekle", icon: "ri-add-circle-line" },
  { label: "Canlı ders hazırla", href: "/ogretmen/canli-dersler", icon: "ri-vidicon-line" },
];

export default function OgretmenDashboard() {
  const { user } = useAuth();
  const identity = resolveTeacherIdentity(user);
  const { data, loading } = useTeacherStats();

  const stats = [
    { icon: "ri-group-line", label: "Öğrenci", value: data?.studentCount, href: "/ogretmen/ogrencilerim" },
    { icon: "ri-book-2-line", label: "Ders", value: data?.courseCount, href: "/ogretmen/derslerim" },
    { icon: "ri-vidicon-line", label: "Canlı Ders", value: data?.liveClassCount, href: "/ogretmen/canli-dersler" },
    { icon: "ri-task-line", label: "Ödev", value: data?.homeworkCount, href: "/ogretmen/odevler" },
  ];

  return (
    <div className="space-y-6">
      <SectionHeader
        title={`Hoş geldin, ${identity.name.split(" ")[0]}`}
        description="Derslerini, öğrencilerini ve canlı derslerini tek panelden yönet."
      />

      {/* Özet istatistikler */}
      <section aria-label="Özet istatistikler">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {stats.map((s) => (
            <Link
              key={s.label}
              to={s.href}
              className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 hover:border-accent-300 transition-colors"
            >
              <div className="flex items-center gap-2.5">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 shrink-0">
                  <i className={`${s.icon} text-lg`} aria-hidden="true"></i>
                </span>
                <p className="text-xs font-medium text-foreground-600">{s.label}</p>
              </div>
              <p className="mt-3 font-heading font-bold text-2xl md:text-3xl text-foreground-950">
                {loading ? "—" : (s.value ?? 0)}
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* Hızlı işlemler */}
      <section aria-label="Hızlı işlemler">
        <h2 className="font-heading font-semibold text-base text-foreground-950">Hızlı işlemler</h2>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {quickActions.map((a) => (
            <Link
              key={a.href}
              to={a.href}
              className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3.5 hover:border-accent-300 transition-colors"
            >
              <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 shrink-0">
                <i className={`${a.icon} text-base`} aria-hidden="true"></i>
              </span>
              <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">{a.label}</span>
              <i className="ri-arrow-right-s-line ml-auto text-foreground-400" aria-hidden="true"></i>
            </Link>
          ))}
        </div>
      </section>

      {/* Branş kartı */}
      <section aria-label="Profil özeti">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5 flex flex-wrap items-center gap-4">
          <span className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-950 text-background-50 font-heading font-bold text-xl">
            {identity.initials}
          </span>
          <div className="min-w-0">
            <p className="font-heading font-semibold text-base text-foreground-950">{identity.name}</p>
            <p className="text-sm text-foreground-500">
              {identity.branch ? `${identity.branch} Öğretmeni` : "Öğretmen"}
              {identity.city ? ` · ${identity.city}` : ""}
            </p>
          </div>
          <Link
            to="/ogretmen/profil"
            className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-800 whitespace-nowrap"
          >
            Profili düzenle
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
      </section>
    </div>
  );
}